import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw, Edit, Trash2, Users, Smartphone, Route, AlertTriangle, Server, FileText, ToggleLeft, ToggleRight, X, ShieldCheck } from 'lucide-react';
import DataTable from '../components/common/DataTable';
import type { Column } from '../components/common/DataTable';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { appDeviceService, routingPolicyService, edgeService } from '../services';
import { dnsService } from '../services/dns.service';
import type { DnsRecord } from '../services/dns.service';
import type { DeviceGroup, DeviceGroupEdgeType, DeviceGroupInput, AccessTunnelSettings, EffectiveAccessConnect } from '../services/appDevice.service';
import accessService, { type EffectiveSignIn, type SignInLevel, type SignInPolicyLimits, type TenantConnectPolicy } from '../services/access.service';
import SignInLifetimeFields from '../components/access/SignInLifetimeFields';
import { FALLBACK_SIGN_IN_LIMITS, defaultsAsEffective, formatDuration, signInLevelError, toGroupOverride } from '../utils/signInPolicy';
import { providerTypeShort } from '../utils/signInRouting';
import type { Edge } from '../services/edge.service';
import type { RoutingPolicy } from '../services/routingPolicy.service';
import { useToast } from '../components/common/ToastContainer';
import StatCard from '../components/common/StatCard';
import { usePermissions } from '../hooks/usePermissions';
import { useTenantStore } from '../stores/tenantStore';
import ApplyDgapTemplateModal from '../components/policies/ApplyDgapTemplateModal';

interface DeviceGroupRow {
  id: string;
  name: string;
  description: string;
  subnetCidr?: string;
  edgeId?: string | null;
  edgeType?: DeviceGroupEdgeType;
  maxDevices: number;
  deviceCount: number;
  createdAt: string;
  routingPolicyIds?: string[];
  dnsVisibility?: 'all' | 'specific';
  dnsRecordCount?: number;
  dnsRecordIds?: string[];
  accessTunnel?: AccessTunnelSettings | null;
  /** Access groups: the connect policy the group's devices receive (the API's reading). */
  accessConnect?: EffectiveAccessConnect | null;
}

interface EdgeOption {
  id: string;
  name: string;
  ip?: string;
  type: 'dedicated' | 'mtge';
}

/** An Access Gateway a group can be pinned to (VSNP-1098). */
interface GatewayOption {
  id: string;
  name: string;
  endpoint: string;
}

/** The edge list rows also carry these (not declared on Edge). */
type EdgeListItem = Edge & { status?: string; name?: string; wanIp?: string };

interface GroupForm {
  name: string;
  description: string;
  edge_id: string;
  edge_type: DeviceGroupEdgeType;
  max_devices: number;
  routingPolicyIds: string[];
  dnsVisibility: 'all' | 'specific';
  dnsRecordIds: string[];
  // Access (v3) groups only — the tunnel the Access client builds. Lists are
  // edited one entry per line.
  tunnelMode: 'full' | 'split';
  tunnelRoutes: string;
  tunnelDnsServers: string;
  tunnelDnsMatchDomains: string;
  tunnelIpv6: 'block' | 'tunnel';
  // Access groups only — the connect policy (doc 28 §4.9, revised
  // 2026-10-02): the tunnel is always on; lockdown (the kill switch, needs a
  // full tunnel), what it does when the tunnel cannot come up, and whether
  // users may disconnect.
  connectLockdown: boolean;
  connectUserCanDisconnect: boolean;
  connectFailMode: 'closed' | 'open';
  // Access groups only — this group's override of the sign-in lifetime
  // policy (doc 28 §4.10); null = the tenant value.
  signIn: SignInLevel;
}

const EMPTY_FORM: GroupForm = {
  name: '',
  description: '',
  edge_id: '',
  edge_type: 'dedicated',
  max_devices: 50,
  routingPolicyIds: [],
  dnsVisibility: 'all',
  dnsRecordIds: [],
  tunnelMode: 'full',
  tunnelRoutes: '',
  tunnelDnsServers: '',
  tunnelDnsMatchDomains: '',
  tunnelIpv6: 'block',
  connectLockdown: true,
  connectUserCanDisconnect: false,
  connectFailMode: 'closed',
  signIn: { reauthInterval: null, tokenLifetime: null },
};

const ANCHORS: Array<{ value: DeviceGroupEdgeType; label: string; tag?: string }> = [
  { value: 'dedicated', label: 'Dedicated Edge' },
  { value: 'mtge', label: 'Shared Gateway (MTGE)' },
  { value: 'access', label: 'Access Gateway', tag: 'ZTNA' },
];

const splitList = (text: string): string[] =>
  text.split(/[\s,]+/).map((v) => v.trim()).filter(Boolean);

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

const CIDR_RE = /^[0-9a-fA-F:.]+\/\d{1,3}$/;

const AppDeviceGroups = () => {
  const toast = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const selectedTenantId = useTenantStore((state) => state.selectedTenantId);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<DeviceGroupRow[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<DeviceGroupRow | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; group: DeviceGroupRow | null }>({
    isOpen: false,
    group: null,
  });
  const [formData, setFormData] = useState<GroupForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [availablePolicies, setAvailablePolicies] = useState<RoutingPolicy[]>([]);
  const [, setLoadingPolicies] = useState(false);
  const [edgeOptions, setEdgeOptions] = useState<EdgeOption[]>([]);
  const [gatewayOptions, setGatewayOptions] = useState<GatewayOption[]>([]);
  const [dnsRecords, setDnsRecords] = useState<DnsRecord[]>([]);
  const [unboundCount, setUnboundCount] = useState(0);
  const [applyTemplateTarget, setApplyTemplateTarget] = useState<DeviceGroupRow | null>(null);
  const [removingPolicy, setRemovingPolicy] = useState(false);
  const [confirmRemovePolicy, setConfirmRemovePolicy] = useState(false);
  const [togglingPolicy, setTogglingPolicy] = useState(false);
  // The tenant's sign-in values (doc 28 §4.10): what a group without its own
  // override inherits. Read when the form opens.
  const [tenantSignIn, setTenantSignIn] = useState<EffectiveSignIn | null>(null);
  const [signInLimits, setSignInLimits] = useState<SignInPolicyLimits>(FALLBACK_SIGN_IN_LIMITS);
  // What the tenant's sign-in route allows (doc 28 §4.9, option A of
  // 2026-10-07): no fail closed when it signs in through an external IdP.
  // null (not loaded, or the call failed) = the form as before; the API
  // still refuses what is not allowed.
  const [tenantConnect, setTenantConnect] = useState<TenantConnectPolicy | null>(null);

  useEffect(() => {
    fetchGroups();
    fetchPolicies();
    fetchEdges();
    fetchGateways();
    fetchUnboundCount();
    fetchDnsRecords();
    void loadTenantConnect();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoadingPolicies(true);
      const response = await routingPolicyService.list({ pageSize: 100 });
      // Show both device_group and dg_template scoped policies (no enabled filter — applied DGAPs start disabled)
      setAvailablePolicies(response.items.filter(
        (p: RoutingPolicy) => p.scope === 'device_group' || p.scope === 'dg_template'
      ));
    } catch (err: unknown) {
      console.error('Failed to fetch routing policies:', err);
    } finally {
      setLoadingPolicies(false);
    }
  };

  const fetchEdges = async () => {
    try {
      // Fetch dedicated edges with App VPN enabled
      const edgesResponse = await edgeService.list({ page: 1, pageSize: 100 });
      const dedicatedOptions: EdgeOption[] = ((edgesResponse.items || []) as EdgeListItem[])
        .filter((e) => e.status !== 'decommissioned')
        .map((e) => ({
          id: e.devSerNum || e.id,
          name: e.devName || e.name || 'Unknown Edge',
          ip: e.wanIp || e.ip,
          type: 'dedicated' as const,
        }));

      // Fetch MTGEs assigned to tenant
      let mtgeOptions: EdgeOption[] = [];
      try {
        const mtgeResponse = await appDeviceService.getMyMtges(selectedTenantId || undefined);
        mtgeOptions = (mtgeResponse.items || [])
          .filter((m) => m.status === 'active' || m.status === 'provisioned')
          .map((m) => ({
            id: m.id,
            name: m.name || 'MTGE',
            ip: m.wanIp || '',
            type: 'mtge' as const,
          }));
      } catch {
        // Non-fatal — MTGEs may not be available
      }

      setEdgeOptions([...dedicatedOptions, ...mtgeOptions]);
    } catch (err: unknown) {
      console.error('Failed to fetch edges:', err);
    }
  };

  // Access Gateways a group may be pinned to (VSNP-1098). A tenant admin sees
  // the gateways assigned to their tenant; the API refuses a pin to a gateway
  // the group's tenant is not assigned to. Offline / degraded are heartbeat
  // states that come back on their own, so only retired gateways are hidden.
  const fetchGateways = async () => {
    try {
      const response = await accessService.listGateways({ limit: 200 });
      setGatewayOptions((response.items || [])
        .filter((g) => g.status !== 'revoked' && g.status !== 'decommissioned')
        .map((g) => ({
        id: g.id,
        name: g.name || g.node_id,
        endpoint: g.public_endpoint || g.fqdn || g.public_ip || '',
      })));
    } catch {
      // Non-fatal — the Access plane may not be set up for this tenant
    }
  };

  const fetchDnsRecords = async () => {
    try {
      const records = await dnsService.listRecords(undefined, selectedTenantId || undefined);
      setDnsRecords(records);
    } catch {
      // DNS not configured — non-fatal
    }
  };

  const fetchUnboundCount = async () => {
    try {
      const result = await appDeviceService.getUnboundGroupCount();
      setUnboundCount(result.count || 0);
    } catch (err: unknown) {
      console.error('Failed to fetch unbound count:', err);
    }
  };

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await appDeviceService.listDeviceGroups({ page: 1, limit: 100 });
      const groupRows: DeviceGroupRow[] = response.items.map((g: DeviceGroup) => ({
        id: g.id,
        name: g.name,
        description: g.description || '',
        subnetCidr: g.subnet_cidr,
        edgeId: g.edge_id,
        edgeType: g.edge_type || 'dedicated',
        maxDevices: g.max_devices || 50,
        deviceCount: g.device_count || 0,
        createdAt: g.createdAt,
        routingPolicyIds: g.routing_policy_ids || [],
        dnsVisibility: g.dns_visibility || 'all',
        dnsRecordCount: g.dns_record_count || 0,
        dnsRecordIds: g.dns_record_ids || [],
        accessTunnel: g.access_tunnel ?? null,
        accessConnect: g.access_connect ?? null,
      }));
      setGroups(groupRows);
    } catch (err: unknown) {
      console.error('Failed to fetch device groups:', err);
      toast.error('Error', 'Failed to fetch device groups');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (group?: DeviceGroupRow) => {
    if (group) {
      setEditingGroup(group);
      const tunnel = group.accessTunnel;
      setFormData({
        name: group.name,
        description: group.description,
        edge_id: group.edgeId || '',
        edge_type: group.edgeType || 'dedicated',
        max_devices: group.maxDevices,
        routingPolicyIds: group.routingPolicyIds || [],
        dnsVisibility: (group.dnsRecordIds && group.dnsRecordIds.length > 0) ? 'specific' : 'all',
        dnsRecordIds: group.dnsRecordIds || [],
        tunnelMode: tunnel?.mode || 'full',
        tunnelRoutes: (tunnel?.routes || []).join('\n'),
        tunnelDnsServers: (tunnel?.dnsServers || []).join('\n'),
        tunnelDnsMatchDomains: (tunnel?.dnsMatchDomains || []).join('\n'),
        tunnelIpv6: tunnel?.ipv6 || 'block',
        // Lockdown defaults on for a full tunnel and is never on for a split one.
        connectLockdown: (tunnel?.mode || 'full') === 'full' && (tunnel?.connect?.lockdown ?? true),
        connectUserCanDisconnect: tunnel?.connect?.userCanDisconnect ?? false,
        // What the devices receive (the API's reading: a fail mode absent is
        // the tenant's default), else the stored choice.
        connectFailMode: group.accessConnect?.failMode ?? tunnel?.connect?.failMode ?? 'closed',
        signIn: {
          reauthInterval: tunnel?.signIn?.reauthInterval ?? null,
          tokenLifetime: tunnel?.signIn?.tokenLifetime ?? null,
        },
      });
    } else {
      setEditingGroup(null);
      setFormData(EMPTY_FORM);
    }
    setIsModalOpen(true);
    void loadTenantSignIn();
    void loadTenantConnect();
  };

  // Whether fail closed is available to the tenant. Failure leaves the form as
  // it was (the API refuses fail closed for an external IdP regardless).
  const loadTenantConnect = async () => {
    try {
      setTenantConnect(await accessService.getConnectPolicy());
    } catch {
      setTenantConnect(null);
    }
  };

  // What a group with no override of its own inherits: the tenant's values
  // (or the defaults). Failure leaves the defaults shown, never blocks the form.
  const loadTenantSignIn = async () => {
    try {
      const view = await accessService.getSignInPolicy();
      setSignInLimits(view);
      setTenantSignIn(view.effective);
    } catch {
      setTenantSignIn(null);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingGroup(null);
    setFormData(EMPTY_FORM);
    setConfirmRemovePolicy(false);
  };

  // The anchor decides the plane (UI-DESIGN §4, Option A). An edge id, an MTGE
  // id and a gateway id are different kinds, so the binding is cleared.
  const handleAnchorChange = (edgeType: DeviceGroupEdgeType) => {
    setFormData(prev => (prev.edge_type === edgeType ? prev : { ...prev, edge_type: edgeType, edge_id: '' }));
  };

  const handleEdgeSelect = (edgeId: string) => {
    const edge = edgeOptions.find(e => e.id === edgeId);
    setFormData(prev => ({
      ...prev,
      edge_id: edgeId,
      edge_type: edge?.type || (prev.edge_type === 'access' ? 'dedicated' : prev.edge_type),
    }));
  };

  const isAccessForm = formData.edge_type === 'access';
  // Option A (2026-10-07): a tenant that signs in through an external IdP has
  // no fail-closed option — a locked-down device could not reach the IdP.
  const failClosedUnavailable = tenantConnect?.failClosedAllowed === false;
  const editingForcedOpen = editingGroup?.accessConnect?.failModeReason === 'external_idp';
  // Domain mode (doc 28d §7.2, §10.1): the tenant routes sign-in by email
  // domain, so fail closed is available, and only the members a provider
  // routes fail open (their browser cannot reach the provider while the kill
  // switch holds traffic). The API counts DEVICES per group whose owner is
  // provider-routed (a user with two devices counts twice), so the web says
  // "devices" (28d §10.1).
  const domainMode = tenantConnect?.routing === 'domains';
  const providerMembersOf = (groupId?: string | null): number => (groupId ? tenantConnect?.providerMembers?.[groupId] ?? 0 : 0);
  const providerWord = providerTypeShort(tenantConnect?.providerType);
  const editingProviderMembers = providerMembersOf(editingGroup?.id);

  const buildAccessTunnel = (): AccessTunnelSettings => ({
    mode: formData.tunnelMode,
    routes: formData.tunnelMode === 'split' ? splitList(formData.tunnelRoutes) : [],
    dnsServers: splitList(formData.tunnelDnsServers),
    dnsMatchDomains: splitList(formData.tunnelDnsMatchDomains).map((d) => d.toLowerCase()),
    ipv6: formData.tunnelIpv6,
    // Always sent, so what the form shows is what is stored. Lockdown needs a
    // full tunnel — the API refuses it with a split one.
    connect: {
      lockdown: formData.tunnelMode === 'full' && formData.connectLockdown,
      // An external-IdP tenant has no choice to make: no fail mode is sent,
      // which is the tenant's default (open), so the group goes back to the
      // safe default (closed) if the tenant returns to a local sign-in. A
      // group that chose open keeps it.
      ...(failClosedUnavailable
        ? (editingGroup?.accessTunnel?.connect?.failMode === 'open' ? { failMode: 'open' as const } : {})
        : { failMode: formData.connectFailMode }),
      userCanDisconnect: formData.connectUserCanDisconnect,
    },
    // Always sent too: {} clears the group's override (doc 28 §4.10).
    signIn: toGroupOverride(formData.signIn),
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Validation Error', 'Group name is required');
      return;
    }

    let accessTunnel: AccessTunnelSettings | undefined;
    if (isAccessForm) {
      accessTunnel = buildAccessTunnel();
      if (accessTunnel.mode === 'split' && accessTunnel.routes.length === 0) {
        toast.error('Validation Error', 'A split tunnel needs at least one route (e.g. 10.0.0.0/8)');
        return;
      }
      const badRoute = accessTunnel.routes.find((r) => !CIDR_RE.test(r));
      if (badRoute) {
        toast.error('Validation Error', `"${badRoute}" is not a CIDR (e.g. 10.0.0.0/8)`);
        return;
      }
      if (accessTunnel.mode === 'split' && accessTunnel.connect?.lockdown) {
        toast.error('Validation Error', 'Lockdown needs a full tunnel');
        return;
      }
      const signInError = signInLevelError(formData.signIn, signInLimits);
      if (signInError) {
        toast.error('Validation Error', signInError);
        return;
      }
    }

    try {
      setSaving(true);
      const payload: DeviceGroupInput & { name: string } = isAccessForm
        ? {
          // Access (v3) anchor: an empty edge_id means "any gateway assigned
          // to the tenant". Routing policies and DNS visibility are v2-only
          // and are left as they are.
          name: formData.name,
          description: formData.description,
          edge_id: formData.edge_id,
          edge_type: 'access',
          max_devices: formData.max_devices,
          access_tunnel: accessTunnel,
        }
        : {
          name: formData.name,
          description: formData.description,
          edge_id: formData.edge_id || undefined,
          edge_type: formData.edge_type,
          max_devices: formData.max_devices,
          routing_policy_ids: formData.routingPolicyIds,
          dns_record_ids: formData.dnsVisibility === 'specific' ? formData.dnsRecordIds : [],
        };
      if (selectedTenantId) {
        payload.tenant_id = selectedTenantId;
      }
      if (editingGroup) {
        await appDeviceService.updateDeviceGroup(editingGroup.id, payload);
        toast.success('Group Updated', 'Device group updated successfully');
      } else {
        await appDeviceService.createDeviceGroup(payload);
        toast.success('Group Created', 'Device group created successfully');
      }
      await fetchGroups();
      await fetchUnboundCount();
      handleCloseModal();
    } catch (err: unknown) {
      toast.error('Error', errorMessage(err, 'Failed to save device group'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (group: DeviceGroupRow) => {
    setDeleteConfirm({ isOpen: true, group });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.group) return;

    try {
      await appDeviceService.deleteDeviceGroup(deleteConfirm.group.id);
      toast.success('Group Deleted', 'Device group deleted successfully');
      await fetchGroups();
      await fetchUnboundCount();
      setDeleteConfirm({ isOpen: false, group: null });
    } catch (err: unknown) {
      toast.error('Delete Failed', errorMessage(err, 'Failed to delete device group'));
      setDeleteConfirm({ isOpen: false, group: null });
    }
  };

  const getCapacityColor = (current: number, max: number) => {
    const pct = max > 0 ? (current / max) * 100 : 0;
    if (pct > 95) return 'text-red-600';
    if (pct > 80) return 'text-amber-600';
    return 'text-gray-900';
  };

  const getEdgeName = (edgeId: string | null | undefined) => {
    if (!edgeId) return null;
    const edge = edgeOptions.find(e => e.id === edgeId);
    return edge?.name || edgeId.substring(0, 8);
  };

  const getGatewayName = (gatewayId: string | null | undefined) => {
    if (!gatewayId) return null;
    return gatewayOptions.find(g => g.id === gatewayId)?.name || gatewayId.substring(0, 8);
  };

  /** The group locks down and fails closed (its own choice; in domain mode the list shows that, not the per-member answer). */
  const groupLocksDownClosed = (group: DeviceGroupRow): boolean => {
    const lockdown = group.accessConnect?.lockdown
      ?? ((group.accessTunnel?.mode ?? 'full') === 'full' && (group.accessTunnel?.connect?.lockdown ?? true));
    return lockdown && (group.accessConnect?.failMode ?? group.accessTunnel?.connect?.failMode ?? 'closed') === 'closed';
  };

  const columns: Column<DeviceGroupRow>[] = [
    {
      key: 'name',
      label: 'Group Name',
      sortable: true,
      render: (group) => (
        <button
          onClick={() => handleOpenModal(group)}
          className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
        >
          <Users className="w-4 h-4 text-primary-600" />
          <div>
            <div className="font-semibold">{group.name}</div>
            {group.description && <div className="text-xs text-gray-500 max-w-[300px] truncate">{group.description}</div>}
          </div>
        </button>
      ),
    },
    {
      key: 'edge',
      label: 'Edge',
      render: (group) => {
        if (group.edgeType === 'access') {
          return (
            <div className="flex items-center space-x-2">
              <ShieldCheck className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">{getGatewayName(group.edgeId) || 'Any gateway'}</span>
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">ZTNA</span>
              {/* Always on (doc 28 §4.9): the kill switch is on unless switched
                  off, and never on for a split tunnel. */}
              {(group.accessConnect?.lockdown
                ?? ((group.accessTunnel?.mode ?? 'full') === 'full' && (group.accessTunnel?.connect?.lockdown ?? true))) && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700">
                  Kill switch{(group.accessConnect?.failMode ?? group.accessTunnel?.connect?.failMode) === 'open' ? ' · fail open' : ''}
                </span>
              )}
              {/* Option A (doc 28 §4.9, 2026-10-07): set to fail closed, but
                  the tenant signs in through an external IdP. */}
              {group.accessConnect?.failModeReason === 'external_idp' && (
                <span
                  className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-warning-100 text-warning-700"
                  title="This group is set to fail closed, but the tenant signs in through an external identity provider, which a locked-down device cannot reach. Its devices fail open."
                  data-testid="dg-fail-open-external-idp"
                >
                  <AlertTriangle className="w-3 h-3" />
                  Fails open: external IdP
                </span>
              )}
              {/* Domain mode (doc 28d §10.1): the group fails closed, except for
                  the members who sign in through an identity provider. */}
              {domainMode && groupLocksDownClosed(group) && providerMembersOf(group.id) > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-warning-100 text-warning-700"
                  title={`This group fails closed, but ${providerMembersOf(group.id)} of its devices belong to users who sign in through ${providerWord}; a locked-down device cannot reach the provider, so those devices fail open.`}
                  data-testid="dg-members-fail-open"
                >
                  <AlertTriangle className="w-3 h-3" />
                  {providerMembersOf(group.id)} device{providerMembersOf(group.id) === 1 ? '' : 's'} fail{providerMembersOf(group.id) === 1 ? 's' : ''} open: identity provider
                </span>
              )}
              {group.accessTunnel?.signIn?.reauthInterval !== undefined && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700">
                  Sign in every {formatDuration(group.accessTunnel.signIn.reauthInterval)}
                </span>
              )}
            </div>
          );
        }
        const edgeName = getEdgeName(group.edgeId);
        return edgeName ? (
          <div className="flex items-center space-x-2">
            <Server className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">{edgeName}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              group.edgeType === 'mtge' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
            }`}>
              {group.edgeType === 'mtge' ? 'MTGE' : 'Edge'}
            </span>
          </div>
        ) : (
          <span className="text-sm text-amber-600 flex items-center space-x-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>Unbound</span>
          </span>
        );
      },
    },
    {
      key: 'subnetCidr',
      label: 'Subnet',
      render: (group) => (
        <span className="text-sm text-gray-600 font-mono">
          {group.subnetCidr || '-'}
        </span>
      ),
    },
    {
      key: 'deviceCount',
      label: 'Devices',
      sortable: true,
      render: (group) => (
        <div className="flex items-center space-x-2">
          <Smartphone className="w-4 h-4 text-gray-400" />
          <span className={`text-sm font-medium ${getCapacityColor(group.deviceCount, group.maxDevices)}`}>
            {group.deviceCount}/{group.maxDevices}
          </span>
        </div>
      ),
    },
    {
      key: 'routingPolicies',
      label: 'Access Policy',
      render: (group) => {
        const policy = availablePolicies.find(p => p.scope === 'device_group' && group.routingPolicyIds?.includes(p.id));
        if (!policy) return <span className="text-sm text-gray-400">None</span>;
        return (
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${policy.enabled === 1 ? 'bg-green-500' : 'bg-gray-400'}`} />
            <span className="text-sm text-gray-700 truncate max-w-[200px]">{policy.name}</span>
          </div>
        );
      },
    },
    {
      key: 'dns',
      label: 'DNS',
      render: (group) => {
        if (group.dnsVisibility === 'specific' && group.dnsRecordCount) {
          return (
            <span className="text-sm text-gray-600">
              {group.dnsRecordCount} record{group.dnsRecordCount !== 1 ? 's' : ''}
            </span>
          );
        }
        return <span className="text-sm text-gray-400">All</span>;
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (group) => (
        <div className="flex items-center justify-end space-x-2">
          {canUpdate && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleOpenModal(group);
              }}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors group"
              title="Edit group"
            >
              <Edit className="w-4 h-4 text-gray-400 group-hover:text-primary-600" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(group);
              }}
              className="p-1.5 hover:bg-red-50 rounded-lg transition-colors group"
              title="Delete group"
            >
              <Trash2 className="w-4 h-4 text-gray-400 group-hover:text-red-600" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Migration Banner */}
      {unboundCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              {unboundCount} device group{unboundCount > 1 ? 's' : ''} require{unboundCount === 1 ? 's' : ''} edge assignment
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Edit each group and select an edge to enable DG-based registration.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">VPN Device Groups</h1>
          <p className="mt-1 text-gray-600">
            Organize your VPN app devices into edge-bound groups
          </p>
        </div>
        <div className="flex space-x-3">
          <button onClick={fetchGroups} disabled={loading} className="btn-secondary inline-flex items-center space-x-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          {canCreate && (
            <button onClick={() => handleOpenModal()} className="btn-primary inline-flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>Create Group</span>
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard icon={Users} title="Total Groups" value={groups.length} color="primary" />
        <StatCard icon={Smartphone} title="Total Devices" value={groups.reduce((sum, g) => sum + g.deviceCount, 0)} color="primary" />
        <StatCard icon={Server} title="Bound to Edges" value={groups.filter(g => g.edgeId || g.edgeType === 'access').length} color="secondary" />
        <StatCard icon={Route} title="With Policy" value={groups.filter(g => availablePolicies.some(p => p.scope === 'device_group' && g.routingPolicyIds?.includes(p.id))).length} color="info" />
      </div>

      {/* Groups Table */}
      <div className="card">
        <DataTable
          columns={columns}
          data={groups}
          loading={loading}
          searchPlaceholder="Search groups..."
          emptyMessage="No device groups found. Create a group to get started."
        />
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingGroup ? 'Edit Device Group' : 'Create Device Group'}
        size="md"
        footer={
          <div className="flex items-center justify-end space-x-3">
            <button onClick={handleCloseModal} className="btn-secondary" disabled={saving}>
              Cancel
            </button>
            {(editingGroup ? canUpdate : canCreate) && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary inline-flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Users className="w-4 h-4" />
                    <span>{editingGroup ? 'Update Group' : 'Create Group'}</span>
                  </>
                )}
              </button>
            )}
          </div>
        }
      >
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Group Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Engineering Team"
              className="input-base w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Devices used by the engineering department"
              rows={2}
              className="input-base w-full"
            />
          </div>

          {/* Anchor — decides the plane (UI-DESIGN §4, Option A; VSNP-1098) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Anchor</label>
            <div className="flex flex-wrap gap-x-4 gap-y-2" role="radiogroup" aria-label="Anchor">
              {ANCHORS.map((a) => (
                <label key={a.value} className="flex items-center space-x-2 text-sm">
                  <input
                    type="radio"
                    name="dg-anchor"
                    checked={formData.edge_type === a.value}
                    onChange={() => handleAnchorChange(a.value)}
                  />
                  <span>{a.label}</span>
                  {a.tag && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{a.tag}</span>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Edge Selector (v2 anchors) */}
          {!isAccessForm && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="flex items-center space-x-1">
                  <Server className="w-4 h-4" />
                  <span>{formData.edge_type === 'mtge' ? 'Shared Gateway (MTGE)' : 'Edge'}</span>
                </span>
              </label>
              <select
                value={formData.edge_id}
                onChange={(e) => handleEdgeSelect(e.target.value)}
                className="input-base w-full"
              >
                <option value="">No edge binding (legacy)</option>
                {edgeOptions.filter(edge => edge.type === formData.edge_type).map(edge => (
                  <option key={edge.id} value={edge.id}>
                    {edge.name}{edge.ip ? ` (${edge.ip})` : ''} — {edge.type === 'mtge' ? 'MTGE' : 'Edge'}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Devices in this group will connect to the selected edge
              </p>
            </div>
          )}

          {/* Access Gateway anchor (v3 plane) */}
          {isAccessForm && (
            <div className="space-y-4 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="flex items-center space-x-1">
                    <ShieldCheck className="w-4 h-4" />
                    <span>Access Gateway</span>
                  </span>
                </label>
                <select
                  value={formData.edge_id}
                  onChange={(e) => setFormData({ ...formData, edge_id: e.target.value })}
                  className="input-base w-full"
                >
                  <option value="">Any gateway assigned to this tenant (ranked)</option>
                  {gatewayOptions.map(g => (
                    <option key={g.id} value={g.id}>
                      {g.name}{g.endpoint ? ` (${g.endpoint})` : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Devices in this group use the SecureLink Secure Access client (QUIC). A pinned gateway is tried first.
                  Apps too old for Secure Access are asked to update.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tunnel</label>
                <div className="flex space-x-4">
                  <label className="flex items-center space-x-2 text-sm">
                    <input
                      type="radio"
                      name="dg-tunnel-mode"
                      checked={formData.tunnelMode === 'full'}
                      // Back to full: the kill switch's default (on) again.
                      onChange={() => setFormData({ ...formData, tunnelMode: 'full', connectLockdown: true })}
                    />
                    <span>Full tunnel</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm">
                    <input
                      type="radio"
                      name="dg-tunnel-mode"
                      checked={formData.tunnelMode === 'split'}
                      // Lockdown needs a full tunnel (doc 28 §4.9).
                      onChange={() => setFormData({ ...formData, tunnelMode: 'split', connectLockdown: false })}
                    />
                    <span>Split tunnel</span>
                  </label>
                </div>
              </div>

              {formData.tunnelMode === 'split' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Routes <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.tunnelRoutes}
                    onChange={(e) => setFormData({ ...formData, tunnelRoutes: e.target.value })}
                    placeholder={'10.0.0.0/8\n192.168.10.0/24'}
                    rows={3}
                    className="input-base w-full font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">One CIDR per line — only these go through the tunnel</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">DNS servers</label>
                  <textarea
                    value={formData.tunnelDnsServers}
                    onChange={(e) => setFormData({ ...formData, tunnelDnsServers: e.target.value })}
                    placeholder="10.0.0.53"
                    rows={2}
                    className="input-base w-full font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">DNS match domains</label>
                  <textarea
                    value={formData.tunnelDnsMatchDomains}
                    onChange={(e) => setFormData({ ...formData, tunnelDnsMatchDomains: e.target.value })}
                    placeholder="corp.example.com"
                    rows={2}
                    className="input-base w-full font-mono text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">IPv6</label>
                <select
                  value={formData.tunnelIpv6}
                  onChange={(e) => setFormData({ ...formData, tunnelIpv6: e.target.value === 'tunnel' ? 'tunnel' : 'block' })}
                  className="input-base w-full sm:w-64"
                >
                  <option value="block">Block (no IPv6 outside the tunnel)</option>
                  <option value="tunnel">Send through the tunnel</option>
                </select>
              </div>

              {/* Connect policy (doc 28 §4.9, revised 2026-10-02): always on */}
              <div className="space-y-2 border-t border-emerald-200 pt-3">
                <label className="block text-sm font-medium text-gray-700">Connection</label>
                <p className="text-xs text-gray-500">
                  Always on: the tunnel starts at logon, on every network change and after a crash, wherever the
                  device is — there are no trusted networks.
                </p>
                <label className="flex items-start space-x-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={formData.tunnelMode === 'full' && formData.connectLockdown}
                    disabled={formData.tunnelMode !== 'full'}
                    onChange={(e) => setFormData({ ...formData, connectLockdown: e.target.checked })}
                    data-testid="dg-connect-lockdown"
                  />
                  <span>
                    <span className="font-medium">Lockdown</span>
                    <span className="text-gray-500">
                      {' '}— kill switch: while the tunnel is down, block everything except the gateways and sign-in
                    </span>
                    {formData.tunnelMode !== 'full' && (
                      <span className="block text-xs text-warning-700">Lockdown needs a full tunnel.</span>
                    )}
                  </span>
                </label>
                {formData.tunnelMode === 'full' && formData.connectLockdown && (
                  <div className="pl-6">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      When the tunnel cannot come up (no gateway for over a minute, sign-in due, waiting for approval,
                      revoked, app too old)
                    </label>
                    <select
                      value={failClosedUnavailable ? 'open' : formData.connectFailMode}
                      onChange={(e) => setFormData({ ...formData, connectFailMode: e.target.value === 'open' ? 'open' : 'closed' })}
                      className="input-base w-full sm:w-96"
                      data-testid="dg-connect-fail-mode"
                    >
                      <option value="closed" disabled={failClosedUnavailable}>
                        Fail closed — keep blocking (sign-in stays reachable){failClosedUnavailable ? ' — not available' : ''}
                      </option>
                      <option value="open">Fail open — release the kill switch until the tunnel is back</option>
                    </select>
                    {failClosedUnavailable && (
                      <p className="mt-1 text-xs text-gray-500" data-testid="dg-fail-closed-unavailable">
                        Fail closed is not available: this tenant signs in through an external identity provider
                        {tenantConnect?.providerType === 'azuread' ? ' (Azure AD / Entra ID)' : tenantConnect?.providerType === 'oidc' ? ' (OIDC)' : ''},
                        and a locked-down device cannot reach it to sign in again. Devices fail open.
                      </p>
                    )}
                    {domainMode && !failClosedUnavailable && formData.connectFailMode === 'closed' && (
                      <p className="mt-1 text-xs text-gray-600 flex items-start gap-1" data-testid="dg-members-fail-open-note">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-warning-600" />
                        <span>
                          {editingProviderMembers > 0
                            ? `${editingProviderMembers} device${editingProviderMembers === 1 ? '' : 's'} in this group belong${editingProviderMembers === 1 ? 's' : ''} to users who sign in through ${providerWord} and fail${editingProviderMembers === 1 ? 's' : ''} open`
                            : `Devices of users who sign in through ${providerWord} fail open`}{' '}
                          (their browser cannot reach the provider while the kill switch holds traffic). Everyone else signs in
                          with their SecureLink password and fails closed.
                        </span>
                      </p>
                    )}
                    {failClosedUnavailable && editingForcedOpen && (
                      <p className="mt-1 text-xs text-warning-700 flex items-start gap-1" data-testid="dg-fail-open-warning">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>
                          This group is set to fail closed, but its devices fail open because the tenant now signs in
                          through an external identity provider. Saving stores the tenant default (fail open).
                        </span>
                      </p>
                    )}
                  </div>
                )}
                <label className="flex items-start space-x-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={formData.connectUserCanDisconnect}
                    onChange={(e) => setFormData({ ...formData, connectUserCanDisconnect: e.target.checked })}
                    data-testid="dg-connect-user-disconnect"
                  />
                  <span>
                    <span className="font-medium">Users can disconnect</span>
                    <span className="text-gray-500"> — show a Disconnect button (off for managed devices)</span>
                  </span>
                </label>
              </div>

              {/* Sign-in lifetime (doc 28 §4.10): this group's override of the tenant value */}
              <div className="space-y-2 border-t border-emerald-200 pt-3">
                <label className="block text-sm font-medium text-gray-700">Sign-in lifetime</label>
                <SignInLifetimeFields
                  key={editingGroup?.id ?? 'new'}
                  value={formData.signIn}
                  onChange={(next) => setFormData((prev) => ({ ...prev, signIn: next }))}
                  inherited={tenantSignIn ?? defaultsAsEffective(signInLimits)}
                  level="group"
                  limits={signInLimits}
                  inheritLabel="Tenant value"
                  testIdPrefix="dg"
                />
                <p className="text-xs text-gray-500">
                  A user&apos;s own setting (VPN user page) wins over this group&apos;s; the tenant&apos;s is under
                  Secure Access → Security.
                </p>
              </div>
            </div>
          )}

          {/* Max Devices */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Max Devices</label>
            <input
              type="number"
              value={formData.max_devices}
              onChange={(e) => setFormData({ ...formData, max_devices: Math.min(253, Math.max(1, parseInt(e.target.value) || 50)) })}
              min={1}
              max={253}
              className="input-base w-32"
            />
            <p className="mt-1 text-xs text-gray-500">
              Maximum devices in this group (1-253, limited by /24 subnet)
            </p>
          </div>

          {/* v2-only settings: shown greyed out for an Access group, whose reach is
              governed by Secure Access → Access Policies (VSNP-1098). */}
          {isAccessForm && (dnsRecords.length > 0 || editingGroup) && (
            <p className="text-xs text-gray-500 rounded-md bg-gray-50 border border-gray-200 p-2">
              DNS record visibility and access policy templates apply to Edge and MTGE groups only.
              What an Access Gateway group can reach is set in{' '}
              <Link to="/access/policies" className="text-primary-600 hover:underline">Secure Access → Access Policies</Link>.
            </p>
          )}
          <fieldset disabled={isAccessForm} className={`space-y-4 ${isAccessForm ? 'opacity-50' : ''}`}>
          {/* Private DNS Visibility */}
          {dnsRecords.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">DNS Record Visibility</label>
              <div className="flex space-x-4 mb-2">
                <label className="flex items-center space-x-2 text-sm">
                  <input
                    type="radio"
                    checked={formData.dnsVisibility === 'all'}
                    onChange={() => setFormData({ ...formData, dnsVisibility: 'all', dnsRecordIds: [] })}
                  />
                  <span>All records visible</span>
                </label>
                <label className="flex items-center space-x-2 text-sm">
                  <input
                    type="radio"
                    checked={formData.dnsVisibility === 'specific'}
                    onChange={() => setFormData({ ...formData, dnsVisibility: 'specific' })}
                  />
                  <span>Specific records only</span>
                </label>
              </div>
              {formData.dnsVisibility === 'specific' && (
                <div className="border rounded-md max-h-40 overflow-y-auto p-2 space-y-1">
                  {dnsRecords.map(r => (
                    <label key={r.id} className="flex items-center space-x-2 text-sm hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={formData.dnsRecordIds.includes(r.id)}
                        onChange={(e) => {
                          const ids = e.target.checked
                            ? [...formData.dnsRecordIds, r.id]
                            : formData.dnsRecordIds.filter(id => id !== r.id);
                          setFormData({ ...formData, dnsRecordIds: ids });
                        }}
                      />
                      <span className="font-mono text-xs">{r.hostname}</span>
                      <span className="text-gray-400 text-xs">{r.fqdn}</span>
                      <span className="text-gray-400 text-xs">{r.ip_address}</span>
                    </label>
                  ))}
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Controls which DNS records are visible in the VPN app's Service Directory for users in this group
              </p>
            </div>
          )}

          {/* Access Policy Management */}
          {editingGroup && (() => {
            const linkedPolicy = availablePolicies.find(
              p => p.scope === 'device_group' && formData.routingPolicyIds.includes(p.id)
            );
            const templateName = linkedPolicy?.source_template_id
              ? availablePolicies.find(p => p.id === linkedPolicy.source_template_id)?.name
              : null;

            return (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="flex items-center space-x-1">
                    <Route className="w-4 h-4" />
                    <span>Access Policy</span>
                  </span>
                </label>

                {linkedPolicy ? (
                  <div className="space-y-3">
                    {/* Policy card */}
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2.5 h-2.5 rounded-full ${linkedPolicy.enabled === 1 ? 'bg-green-500' : 'bg-gray-400'}`} />
                          <span className="text-sm font-semibold text-gray-900">{linkedPolicy.name}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            linkedPolicy.enabled === 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {linkedPolicy.enabled === 1 ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">{linkedPolicy.rules?.length || 0} rules</span>
                      </div>
                      {templateName && (
                        <p className="text-xs text-gray-500 mt-1">From template: {templateName}</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-3">
                      {/* Enable/Disable toggle */}
                      <button
                        type="button"
                        disabled={togglingPolicy}
                        onClick={async () => {
                          try {
                            setTogglingPolicy(true);
                            await routingPolicyService.update(linkedPolicy.id, {
                              enabled: linkedPolicy.enabled === 1 ? 0 : 1,
                            });
                            toast.success('Policy Updated', linkedPolicy.enabled === 1 ? 'Policy disabled' : 'Policy enabled');
                            await fetchPolicies();
                            await fetchGroups();
                          } catch (err: unknown) {
                            toast.error('Error', errorMessage(err, 'Failed to toggle policy'));
                          } finally {
                            setTogglingPolicy(false);
                          }
                        }}
                        className="inline-flex items-center space-x-1.5 text-sm text-gray-600 hover:text-primary-600 transition-colors"
                      >
                        {linkedPolicy.enabled === 1 ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4" />}
                        <span>{linkedPolicy.enabled === 1 ? 'Disable' : 'Enable'}</span>
                      </button>

                      {/* Remove Policy */}
                      {!confirmRemovePolicy ? (
                        <button
                          type="button"
                          onClick={() => setConfirmRemovePolicy(true)}
                          className="inline-flex items-center space-x-1.5 text-sm text-red-600 hover:text-red-700 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          <span>Remove</span>
                        </button>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-red-600">Remove policy?</span>
                          <button
                            type="button"
                            disabled={removingPolicy}
                            onClick={async () => {
                              try {
                                setRemovingPolicy(true);
                                const result = await appDeviceService.removeAccessPolicy(editingGroup.id);
                                toast.success('Policy Removed', result.removedPolicyName
                                  ? `"${result.removedPolicyName}" removed`
                                  : 'Access policy removed');
                                setConfirmRemovePolicy(false);
                                setFormData(prev => ({ ...prev, routingPolicyIds: [] }));
                                await fetchPolicies();
                                await fetchGroups();
                              } catch (err: unknown) {
                                toast.error('Error', errorMessage(err, 'Failed to remove policy'));
                              } finally {
                                setRemovingPolicy(false);
                              }
                            }}
                            className="text-xs font-medium text-red-700 hover:text-red-800"
                          >
                            {removingPolicy ? 'Removing...' : 'Yes'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmRemovePolicy(false)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            No
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Replace with template (removes current, then opens apply modal) */}
                    {editingGroup.edgeId && (
                      <button
                        type="button"
                        disabled={removingPolicy}
                        onClick={async () => {
                          try {
                            setRemovingPolicy(true);
                            await appDeviceService.removeAccessPolicy(editingGroup.id);
                            await fetchPolicies();
                            await fetchGroups();
                            handleCloseModal();
                            setApplyTemplateTarget(editingGroup);
                          } catch (err: unknown) {
                            toast.error('Error', errorMessage(err, 'Failed to remove existing policy'));
                          } finally {
                            setRemovingPolicy(false);
                          }
                        }}
                        className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        Replace with different template...
                      </button>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-500 mb-3">No access policy applied.</p>
                    {editingGroup.edgeId ? (
                      canUpdate && <button
                        type="button"
                        onClick={() => {
                          handleCloseModal();
                          setApplyTemplateTarget(editingGroup);
                        }}
                        className="btn-secondary inline-flex items-center space-x-2 text-sm"
                      >
                        <FileText className="w-4 h-4" />
                        <span>Apply Access Policy Template</span>
                      </button>
                    ) : (
                      <p className="text-xs text-amber-600">Bind this group to an edge first to apply access policy templates.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          </fieldset>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, group: null })}
        onConfirm={confirmDelete}
        title="Delete Device Group"
        message={
          deleteConfirm.group
            ? `Are you sure you want to delete "${deleteConfirm.group.name}"? ${deleteConfirm.group.deviceCount > 0
              ? `This group has ${deleteConfirm.group.deviceCount} device(s). Devices will be unassigned but not deleted.`
              : 'This action cannot be undone.'
            }`
            : ''
        }
        confirmText="Delete"
        variant="danger"
      />

      {/* Apply DGAP Template Modal */}
      {applyTemplateTarget && (
        <ApplyDgapTemplateModal
          isOpen={!!applyTemplateTarget}
          onClose={() => setApplyTemplateTarget(null)}
          groupId={applyTemplateTarget.id}
          groupName={applyTemplateTarget.name}
          edgeGuid={applyTemplateTarget.edgeId}
          onApplied={() => {
            setApplyTemplateTarget(null);
            fetchGroups();
            fetchPolicies();
          }}
        />
      )}
    </div>
  );
};

export default AppDeviceGroups;
